import React from 'react'
import { Box, Button, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'

// From/To filter for table views. Values are plain "YYYY-MM-DD" strings, which
// is what the native date input produces and what the API expects.
function DateRangeFilter(props) {
  const {
    startDate = '',
    endDate = '',
    onChange,
    // 'small' matches the SearchBar pill in table headers; 'medium' lines up
    // with full-height form controls such as Select.
    size = 'small',
    showClear = true
  } = props
  const theme = useTheme()
  const { t } = useTranslation()
  const isMedium = size === 'medium'
  const controlHeight = isMedium ? 38 : 29
  const fontSize = isMedium ? 12 : 11

  const emitChange = (nextStart, nextEnd) => {
    if (typeof onChange === 'function') onChange(nextStart, nextEnd)
  }

  const onStartChange = event => {
    const nextStart = event.target.value
    // Keep the range coherent: a start past the current end drags the end along.
    const nextEnd =
      nextStart && endDate && nextStart > endDate ? nextStart : endDate
    emitChange(nextStart, nextEnd)
  }

  const onEndChange = event => {
    const nextEnd = event.target.value
    const nextStart =
      nextEnd && startDate && nextEnd < startDate ? nextEnd : startDate
    emitChange(nextStart, nextEnd)
  }

  // Matches the SearchBar pill: same height, radius, font and muted text, so the
  // two controls read as one row of filters.
  const inputSx = {
    backgroundColor: theme.palette.background.primary,
    border: isMedium ? `1px solid ${theme.palette.grey[300]}` : 'none',
    outline: 'none',
    height: controlHeight,
    boxSizing: 'border-box',
    borderRadius: isMedium ? '6px' : '15px',
    padding: isMedium ? '0 10px' : '5px 10px',
    fontSize,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.disabled,
    colorScheme: 'light',
    cursor: 'pointer',
    // Tint the browser's calendar glyph towards the brand orange so it matches
    // the search icon sitting beside it.
    '&::-webkit-calendar-picker-indicator': {
      cursor: 'pointer',
      opacity: 0.85,
      filter:
        'invert(52%) sepia(72%) saturate(2400%) hue-rotate(346deg) brightness(99%) contrast(92%)'
    },
    '&:focus': {
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
    }
  }

  const labelSx = {
    fontSize,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    color: theme.palette.text.disabled
  }

  const hasRange = Boolean(startDate || endDate)

  return (
    <Box
      className={props.className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 1
      }}>
      <Typography sx={labelSx}>{t('From')}</Typography>
      <Box
        component="input"
        type="date"
        value={startDate}
        onChange={onStartChange}
        max={endDate || undefined}
        sx={inputSx}
      />
      <Typography sx={labelSx}>{t('To')}</Typography>
      <Box
        component="input"
        type="date"
        value={endDate}
        onChange={onEndChange}
        min={startDate || undefined}
        sx={inputSx}
      />
      {showClear && (
        <Button
          size="small"
          disableRipple
          onClick={() => emitChange('', '')}
          sx={{
            fontSize: 11,
            fontWeight: 500,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            minWidth: 'auto',
            padding: '2px 8px',
            borderRadius: '15px',
            color: theme.palette.primary.main,
            // Held in place rather than unmounted so the row does not reflow when
            // a range is picked or cleared.
            visibility: hasRange ? 'visible' : 'hidden',
            '&:hover': {
              backgroundColor: theme.palette.background.primary
            }
          }}>
          {t('Clear')}
        </Button>
      )}
    </Box>
  )
}

export default DateRangeFilter
